import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { LaunchDarklyApi } from './LaunchDarklyApi';
import {
  EvaluationUsageType,
  FeatureFlagType,
  ListEnvironmentType,
  ListFeatureFlagType,
} from '../types';

const buildQueryParams = (
  params: Record<string, string | undefined>,
): string => {
  const searchParams = new URLSearchParams();

  // Add only non-empty parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });

  return searchParams.toString();
};

export class LaunchDarklyClient implements LaunchDarklyApi {
  private discoveryApi: DiscoveryApi;
  private fetchApi: FetchApi;

  public constructor({
    discoveryApi,
    fetchApi,
  }: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.fetchApi = fetchApi;
    this.discoveryApi = discoveryApi;
  }

  async callApi<T>({
    input,
    init
  }: {
    input: string;
    init?: RequestInit;
  }): Promise<T> {
    const proxyUri = `${await this.discoveryApi.getBaseUrl('proxy')}/launchdarkly/api`;
    const resp = await this.fetchApi.fetch(`${proxyUri}/${input}`, init)
  
    if (!resp.ok) {
      throw new Error(resp.statusText)};
      
    return resp.json();
  }

  async getEvaluationsUsage({
    featureFlagKey,
    projectKey,
    environmentKey,
    from,
    to,
    tz,
  }: {
    projectKey: string;
    featureFlagKey: string;
    environmentKey: string;
    from?: string;
    to?: string;
    tz?: string;
  }): Promise<EvaluationUsageType> {
    const params = buildQueryParams({
      from: from,
      to: to,
      tz: tz,
    });
    const evaluationUsage = await this.callApi<EvaluationUsageType>({
      input:
        `usage/evaluations/${projectKey}/${environmentKey}/${featureFlagKey}?` +
        params.toString(),
      init: {
        method: 'GET'
      },
    });

    return evaluationUsage;
  }

  async getFeatureFlag({
    featureFlagKey,
    projectKey,
  }: {
    projectKey: string;
    featureFlagKey: string;
  }): Promise<FeatureFlagType> {
    const featureFlag = await this.callApi<FeatureFlagType>({
      input: `flags/${projectKey}/${featureFlagKey}`,
      init: {
        method: 'GET'
      },
    });
    return featureFlag;
  }

  async getListEnvironments({
    projectKey,
  }: {
    projectKey: string | 'default';
  }): Promise<ListEnvironmentType> {
    const listEnvironment = await this.callApi<ListEnvironmentType>({
      input: `projects/${projectKey}/environments`,
      init: {
        method: 'GET'
      },
    });

    return listEnvironment;
  }

  async getListFeatureFlag({
    projectKey,
    env,
    offset
  }: {
    projectKey: string;
    env: string;
    offset: number
  }): Promise<ListFeatureFlagType> {
    const listFeatureFlag = await this.callApi<ListFeatureFlagType>({
      input: `flags/${projectKey}?limit=6&offset=${offset}&env=${env}`,
      init: {
        method: 'GET'
      },
    });

    return listFeatureFlag;
  }
}
